import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useComplaintModal } from "../context/ComplaintModalContext";
import Header from "../components/Header";
import SearchBar from "../components/SearchBar";
import ComplaintList from "../components/ComplaintList";
import MostComplainedCompanies from "../components/MostComplainedCompanies";
import Filters from "../components/Filters";
import CompanyRanking from "../components/CompanyRanking";
import { listarDenuncias, obterIndicadores, listarVagas } from "../services/api";

// "Mais de 7.500 vagas" — arredondado para BAIXO, igual ao servidor (SeoMeta.MaisDeVagas): nunca promete mais
// do que existe, e não envelhece quando o mural cresce.
const maisDeVagas = (n) => {
  if (!n) return null;
  if (n < 100) return `${n} vagas`;
  const passo = n >= 2000 ? 500 : 100;
  return `Mais de ${(Math.floor(n / passo) * passo).toLocaleString("pt-BR")} vagas`;
};

// Categorias de desrespeito (sugestão da comunidade): dão NOME à dor e levam ao /denunciar pré-selecionado.
const CATEGORIAS = [
  { emoji: "👻", titulo: "Sumiram depois da entrevista", desc: "Você foi bem, prometeram retorno… e nunca mais responderam.", tipo: 0 },
  { emoji: "🔓", titulo: "Pediram dados demais", desc: "CPF, nome da mãe, documentos — só para se candidatar. A LGPD existe.", tipo: 1 },
  { emoji: "💸", titulo: "Oferta abaixo do combinado", desc: "Conduziram todo o processo sabendo que o salário não fechava — ou fizeram proposta nitidamente exploratória.", tipo: 1 },
  { emoji: "🌫️", titulo: "Vaga sem transparência", desc: "Salário, regime (PJ/CLT) e condições só apareceram no fim — ou nunca.", tipo: 3 },
  { emoji: "🐌", titulo: "Processo arrastado e mudo", desc: "Semanas de silêncio entre as etapas, sem nenhuma comunicação.", tipo: 2 },
  { emoji: "🙅", titulo: "Desrespeito na entrevista", desc: "Comentários inadequados, perguntas invasivas, tratamento com desdém.", tipo: 1 },
  { emoji: "🧪", titulo: "Case que virou trabalho de graça", desc: "Pediram um \"teste\" que era entrega real — sem pagar, sem vaga garantida — e ainda usaram o seu projeto.", tipo: 1 },
];

function HomePage() {
  const [denuncias, setDenuncias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState({ empresa: "", status: null, cidade: "", setor: "", natureza: null });
  const [ordem, setOrdem] = useState("recentes"); // 'recentes' | 'confirmados'
  const [stats, setStats] = useState(null); // números reais p/ a barra de prova social
  const navigate = useNavigate();
  const { openComplaint } = useComplaintModal();

  const denunciasOrdenadas = useMemo(() => {
    const arr = [...denuncias];
    // Recência = quando o relato foi PUBLICADO no site (criadoEm), não a data do processo que o usuário informou —
    // assim um relato novo sobre um processo antigo aparece no topo, como o usuário espera de "Mais recentes".
    const dt = (x) => new Date(x.criadoEm);
    if (ordem === "confirmados") {
      arr.sort((a, b) =>
        (b.totalConfirmacoes || 0) - (a.totalConfirmacoes || 0) ||
        dt(b) - dt(a));
    } else {
      arr.sort((a, b) => dt(b) - dt(a)); // mais recentes (publicados) primeiro
    }
    return arr;
  }, [denuncias, ordem]);

  const reqSeq = useRef(0); // guard contra resposta fora de ordem (carga inicial pesada x busca/filtro leve)

  const carregar = useCallback(async (f) => {
    const seq = ++reqSeq.current;
    setCarregando(true);
    setErro(null);
    try {
      const data = await listarDenuncias(f ?? { empresa: "", status: null, cidade: "", setor: "", natureza: null });
      if (seq === reqSeq.current) setDenuncias(data);
    } catch (e) {
      if (seq === reqSeq.current) setErro(e.message || "Falha ao carregar relatos.");
    } finally {
      if (seq === reqSeq.current) setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { obterIndicadores().then((d) => setStats(d?.geral || d)).catch(() => {}); }, []);
  // Total do mural para a abertura. take=1: só interessa o "total", não as vagas.
  const [totalVagas, setTotalVagas] = useState(null);
  useEffect(() => { listarVagas({ take: 1 }).then((r) => setTotalVagas(r?.total ?? null)).catch(() => {}); }, []);

  const aplicarFiltros = (novos) => {
    const f = { ...filtros, ...novos };
    setFiltros(f);
    carregar(f);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-6xl mx-auto px-4 md:px-6">
        <section className="pt-8 md:pt-12 pb-6">
          <h1 className="text-2xl md:text-4xl font-bold text-text">
            Levou um vácuo do RH?
          </h1>
          {/* 🧭 VAGAS NA ABERTURA (19/09/2026): o eaiRH é vaga + reputação, e a abertura só falava da
              reputação — uma IA perguntada sobre o site descreveu só essa metade. */}
          <p className="mt-2 text-gray-600 max-w-3xl">
            <strong className="text-primary">{maisDeVagas(totalVagas) || "Vagas"} de emprego</strong> — e, ao lado de cada uma, se a empresa costuma dar retorno a quem se candidata. Descubra <strong className="text-primary">quais empresas dão retorno</strong> — e quais somem — antes de investir seu tempo, e transforme o silêncio que você levou em um alerta pro próximo candidato.
          </p>

          <ProvaSocial stats={stats} />

          <div className="mt-6">
            <SearchBar
              placeholder="Pesquise uma empresa"
              onSearch={(empresa) => aplicarFiltros({ empresa })}
            />
          </div>

          <Caminhos />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {/* Lista */}
            <div className="md:col-span-2">
              {carregando && <p className="text-gray-500">Carregando relatos...</p>}
              {erro && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                  <p>Não foi possível carregar os relatos no momento.</p>
                  <button type="button" onClick={() => carregar(filtros)}
                    className="mt-2 inline-flex items-center rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700">
                    Tentar novamente
                  </button>
                </div>
              )}
              {!carregando && !erro && denuncias.length > 0 && (
                <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-sm text-gray-500">{denuncias.length} relato(s)</span>
                  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                    <OrdenarBtn ativo={ordem === "recentes"} onClick={() => setOrdem("recentes")}>Mais recentes</OrdenarBtn>
                    <OrdenarBtn ativo={ordem === "confirmados"} onClick={() => setOrdem("confirmados")}>Mais confirmados</OrdenarBtn>
                  </div>
                </div>
              )}
              {!carregando && !erro && (
                <ComplaintList
                  complaints={denunciasOrdenadas}
                  onSelect={(d) => navigate(`/relato/${d.id}`)}
                />
              )}
            </div>

            {/* Sidebar */}
            <aside className="md:col-span-1 space-y-6">
              <Filters onChange={aplicarFiltros} valores={filtros} />
              <CompanyRanking onSelect={(id) => navigate(`/empresas/${id}`)} />
              <MostComplainedCompanies onSelect={(id) => navigate(`/empresas/${id}`)} />
            </aside>
          </div>
        </section>

        {/* Categorias de desrespeito — dá nome à dor e leva ao relato (ideia da comunidade no LinkedIn) */}
        <section className="mt-4 mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-text">Passou por alguma dessas?</h2>
          <p className="mt-1 text-gray-600">Dar nome ao que aconteceu já é um alívio — e ajuda o próximo. Escolha o que você viveu e conte em 2 minutos (anônimo, se quiser).</p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIAS.map((c) => (
              <button key={c.titulo} type="button" onClick={() => openComplaint({ tipo: c.tipo })}
                className="group block w-full text-left rounded-2xl border border-gray-200 bg-white p-5 shadow-sm hover:border-primary hover:shadow-md transition">
                <div className="text-2xl" aria-hidden>{c.emoji}</div>
                <h3 className="mt-2 font-bold text-gray-900">{c.titulo}</h3>
                <p className="mt-1 text-sm text-gray-600">{c.desc}</p>
                <span className="mt-3 inline-block text-sm font-semibold text-primary group-hover:underline">Relatar isso →</span>
              </button>
            ))}
          </div>
        </section>

        {/* CTA banner */}
        <section className="mt-10 mb-14">
          <div className="bg-text text-white rounded-2xl px-6 py-6 md:px-8 md:py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h3 className="text-xl md:text-2xl font-semibold">
              Cansou de mandar currículo e só ouvir o silêncio?
            </h3>
            <button
              type="button"
              onClick={() => openComplaint()}
              className="inline-flex items-center justify-center rounded-xl bg-orange-600 hover:bg-orange-700 transition-colors px-5 py-3 font-semibold"
            >
              Quero registrar meu relato
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

// Barra de prova social: números reais (via /api/empresas/indicadores) — credibilidade imediata p/ quem chega.
function ProvaSocial({ stats }) {
  if (!stats) return null;
  const itens = [
    { e: "📣", n: stats.totalDenuncias, l: "relatos de candidatos" },
    { e: "🏢", n: stats.totalEmpresas, l: "empresas avaliadas" },
    { e: "👻", n: `${Math.round(stats.percSemResposta || 0)}%`, l: "dos processos somem sem resposta", warn: true },
    { e: "⭐", n: Number(stats.notaMediaGeral || 0).toFixed(1), l: "nota média das empresas" },
  ];
  return (
    <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
      {itens.map((x, i) => (
        <div key={i} className="rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-sm text-center">
          <div className="text-2xl leading-none" aria-hidden>{x.e}</div>
          <div className={`mt-1.5 text-2xl md:text-3xl font-extrabold ${x.warn ? "text-orange-600" : "text-primary"}`}>{x.n}</div>
          <div className="text-xs text-gray-500 mt-0.5">{x.l}</div>
        </div>
      ))}
    </div>
  );
}

// Três caminhos: converte quem chegou curioso e NÃO veio reclamar (pesquisar empresa / candidato / empresa).
function Caminhos() {
  const navigate = useNavigate();
  const cards = [
    { ico: "🔎", h: "Pesquise uma empresa", p: "Antes de se candidatar, veja como ela trata candidatos: dá retorno? Some? Quanto demora?", cta: "Ver empresas avaliadas →", to: "/empresas" },
    { ico: "💼", h: "Candidato? Monte seu perfil", p: "Receba as vagas mais compatíveis com você — já com a reputação de cada empresa.", cta: "Criar meu perfil →", to: "/curriculo" },
    // Antes apontava para /como-funciona, que EXPLICA o direito de resposta mas nao abre porta nenhuma:
    // quem queria publicar vaga ou integrar o ATS nao tinha para onde ir, e as duas parcerias existentes
    // nasceram de conversa pessoal do Nelson. A porta agora existe.
    { ico: "🏢", h: "É empresa?", p: "Publique vagas, responda aos relatos e avise cada candidato automaticamente.", cta: "Ver como participar →", to: "/para-empresas" },
  ];
  return (
    <section className="mt-8">
      <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">Não veio reclamar? Você também pode…</h2>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <button key={c.h} type="button" onClick={() => navigate(c.to)}
            className="group text-left rounded-2xl border border-gray-200 bg-white p-5 shadow-sm hover:border-primary hover:shadow-md transition">
            <div className="text-3xl" aria-hidden>{c.ico}</div>
            <h3 className="mt-3 font-bold text-gray-900">{c.h}</h3>
            <p className="mt-1 text-sm text-gray-600">{c.p}</p>
            <span className="mt-3 inline-block text-sm font-semibold text-primary group-hover:underline">{c.cta}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function OrdenarBtn({ ativo, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      className={`px-3 py-1.5 font-semibold transition ${ativo ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
    >
      {children}
    </button>
  );
}

export default HomePage;
